"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useModalDialog } from "@/hooks/useModalDialog";

export type GalleryImage = {
  src: string;
  alt: string;
  label: string;
  width: number;
  height: number;
};

export type ProjectGalleryImages = readonly [
  GalleryImage,
  ...GalleryImage[],
];

type ProjectGalleryProps = {
  images: ProjectGalleryImages;
  projectName: string;
  status: string;
  priority?: boolean;
};

export function ProjectGallery({
  images,
  projectName,
  status,
  priority = false,
}: ProjectGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useModalDialog<HTMLDivElement>({
    isOpen,
    onClose: closeGallery,
    initialFocusRef: closeButtonRef,
  });

  const openImage = (index: number) => {
    setActiveIndex(index);
    setIsOpen(true);
  };

  const showPrevious = () =>
    setActiveIndex((index) => (index - 1 + images.length) % images.length);
  const showNext = () => setActiveIndex((index) => (index + 1) % images.length);

  function closeGallery() {
    setIsOpen(false);
  }

  useEffect(() => {
    if (!isOpen || images.length < 2) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft")
        setActiveIndex((index) => (index - 1 + images.length) % images.length);
      if (event.key === "ArrowRight")
        setActiveIndex((index) => (index + 1) % images.length);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, images.length]);

  useEffect(() => {
    if (!isOpen || images.length < 2) {
      return;
    }

    const nextImage = images[(activeIndex + 1) % images.length];
    const preload = new window.Image();

    preload.src = nextImage.src;
  }, [activeIndex, images, isOpen]);

  const activeImage = images[activeIndex];

  return (
    <div className="case-media" aria-label={`Скриншоты проекта ${projectName}`}>
      <button
        className="case-visual gallery-trigger"
        type="button"
        onClick={() => openImage(0)}
        aria-label={`Открыть скриншоты проекта ${projectName}`}
      >
        <Image
          className="project-shot"
          src={images[0].src}
          alt={images[0].alt}
          fill
          priority={priority}
          sizes="(max-width: 720px) 100vw, 62vw"
        />
        <span className="gallery-open-hint">
          Во весь размер <b aria-hidden="true">↗</b>
        </span>
        <span className={`case-live ${status !== "LIVE" ? "progress" : ""}`}>
          <i /> {status}
        </span>
      </button>

      <div className="case-thumbnails four">
        {images.slice(1).map((image, index) => (
          <button
            className="case-thumb-image gallery-thumb"
            type="button"
            onClick={() => openImage(index + 1)}
            key={image.src}
            aria-label={`Открыть: ${image.label}`}
          >
            <Image
              src={image.src}
              alt={image.alt}
              fill
              sizes="(max-width: 720px) 50vw, 16vw"
            />
            <span className="shot-label">{image.label}</span>
          </button>
        ))}
      </div>

      {isOpen && (
        <div
          className="image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Галерея проекта ${projectName}`}
          ref={dialogRef}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeGallery();
            }
          }}
        >
          <div className="lightbox-header">
            <div>
              <span>{projectName}</span>
              <b>
                {activeIndex + 1} / {images.length}
              </b>
            </div>
            <a href={activeImage.src} target="_blank" rel="noreferrer">
              Открыть оригинал ↗
            </a>
            <button
              type="button"
              onClick={closeGallery}
              aria-label="Закрыть галерею"
              ref={closeButtonRef}
            >
              ×
            </button>
          </div>

          <div className="lightbox-stage">
            {images.length > 1 && (
              <button
                className="lightbox-arrow previous"
                type="button"
                onClick={showPrevious}
                aria-label="Предыдущий скриншот"
              >
                ←
              </button>
            )}
            <div className="lightbox-image">
              <Image
                key={activeImage.src}
                src={activeImage.src}
                alt={activeImage.alt}
                width={activeImage.width}
                height={activeImage.height}
                loading="eager"
                unoptimized
              />
            </div>
            {images.length > 1 && (
              <button
                className="lightbox-arrow next"
                type="button"
                onClick={showNext}
                aria-label="Следующий скриншот"
              >
                →
              </button>
            )}
          </div>

          <p className="lightbox-caption">{activeImage.label}</p>
        </div>
      )}
    </div>
  );
}
